import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { FingerPrintIcon, CodeBracketIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline';
import useText from '@/hooks/useText';
import { FoundationsSection } from '@/components/AboutDISGENET/FoundationsSection';
import { useAccessTypes } from '@/components/AboutDISGENET/helpers';
import { StickyDnaScrollSection } from './StickyDnaScrollSection';
import type { StickyDnaStep } from './StickyDnaScrollSection';
import './scrollytelling.css';

const dnaColorVars = {
  '--dna-magenta': 'rgb(209, 0, 145)',
  '--dna-navy': 'rgb(13, 23, 96)',
  '--dna-gray': 'rgb(192, 195, 203)',
} as CSSProperties;

function AboutScrollytelling() {
  const heroTitle = useText('about_disgenet.scrolly.hero_title');
  const heroIntro = useText('about_disgenet.scrolly.hero_intro');

  const s3t = useText('about_disgenet.scrolly.sticky_3_title');
  const s4t = useText('about_disgenet.scrolly.sticky_4_title');

  const stickySteps: StickyDnaStep[] = [
    { id: 's3', title: s3t },
    { id: 's4', title: s4t },
  ];

  const sourcesSectionTitle = useText('about_disgenet.scrolly.sources_section_title');
  const src1b = useText('about_disgenet.scrolly.source_card_1_body');
  const src2b = useText('about_disgenet.scrolly.source_card_2_body');
  const src3b = useText('about_disgenet.scrolly.source_card_3_body');
  const src4b = useText('about_disgenet.scrolly.source_card_4_body');
  const dGenomic = useText('about_disgenet.hero.diagram.genomic');
  const dBiobanks = useText('about_disgenet.hero.diagram.biobanks');
  const dTrials = useText('about_disgenet.hero.diagram.trials');
  const dLiterature = useText('about_disgenet.hero.diagram.literature');

  const hintEyebrow = useText('about_disgenet.scrolly.hint_section_eyebrow');
  const hintMainText = useText('about_disgenet.hero.hint');
  const docHintPrefix = useText('about_disgenet.hero.documentation_hint_prefix');
  const docHintLink = useText('about_disgenet.hero.documentation_hint_link');
  const docHintSuffix = useText('about_disgenet.hero.documentation_hint_suffix');
  const toolsHintPrefix = useText('about_disgenet.scrolly.tools_hint_prefix');
  const toolsHintLink = useText('about_disgenet.scrolly.tools_hint_link');

  const platformsEyebrow = useText('about_disgenet.scrolly.platforms_section_eyebrow');
  const powerTitle = useText('about_disgenet.use_cases.section_title');
  const powerSubtitle = useText('about_disgenet.use_cases.section_subtitle');
  const fitPrefix = useText('about_disgenet.use_cases.fit_prefix');
  const fitHighlight = useText('about_disgenet.use_cases.fit_highlight');
  const fitSuffix = useText('about_disgenet.use_cases.fit_suffix');

  const accessTypes = useAccessTypes();
  const platforms = accessTypes.map(a => ({
    id: String(a.id),
    title: a.title,
    description: a.description,
    hint: a.hint,
    icon: a.icon,
  }));

  const extraBubbles = [
    {
      id: 'extra1',
      title: 'Install',
      description: 'R Package',
      hint: 'Explore DISGENET',
      icon: (
        <CodeBracketIcon style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }} />
      ),
    },
    {
      id: 'extra2',
      title: 'Download',
      description: 'Cytoscape',
      hint: 'Analize the data',
      icon: (
        <CloudArrowDownIcon style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }} />
      ),
    },
  ];

  const sourceCards = [
    { id: 'src1', title: dGenomic, body: src1b },
    { id: 'src2', title: dBiobanks, body: src2b },
    { id: 'src3', title: dTrials, body: src3b },
    { id: 'src4', title: dLiterature, body: src4b },
  ];

  return (
    <article className="about-page about-page--scrolly" style={dnaColorVars}>
      <header className="scrolly-hero">
        <div className="scrolly-hero__content">
          <p className="about-page__eyebrow scrolly-hero__eyebrow">DISGENET</p>
          <h1 className="scrolly-hero__title">{heroTitle}</h1>
          <p className="scrolly-hero__intro">{heroIntro}</p>
          <div className="scrolly-hero__scroll-hint" aria-hidden>
            <span className="scrolly-hero__scroll-line" />
            <span className="scrolly-hero__scroll-label">scroll</span>
          </div>
        </div>
      </header>

      <div className="about-page__shell about-page__shell--scrolly">
        <StickyDnaScrollSection
          steps={stickySteps}
          sourcesSectionTitle={sourcesSectionTitle}
          sourceCards={sourceCards}
          hintEyebrow={hintEyebrow}
          hintMainText={hintMainText}
          hintLeadIcon={<FingerPrintIcon className="scrolly-s3-fingerprint-icon" />}
          hintDocNote={
            <>
              {docHintPrefix}
              <Link to="/docs" className="scrolly-s3-doc-link">
                {docHintLink}
              </Link>
              {docHintSuffix}
            </>
          }
          platformsEyebrow={platformsEyebrow}
          powerTitle={powerTitle}
          powerSubtitle={powerSubtitle}
          fitPrefix={fitPrefix}
          fitHighlight={fitHighlight}
          fitSuffix={fitSuffix}
          toolsLink={
            <>
              {toolsHintPrefix} <Link to="/tools" className="scrolly-s3-doc-link">{toolsHintLink}</Link>
            </>
          }
          platforms={platforms}
          extraBubbles={extraBubbles}
          overview={<FoundationsSection />}
          sectionVh={1700}
        />
      </div>
    </article>
  );
}

export default AboutScrollytelling;
